/**
 * Units Mapping Selection Hook
 * Manages unit selection state and validation for mapping
 * Includes localStorage persistence for selections to survive page navigation
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ChannelType,
  ChannelName,
  IUnitSelection,
  IMapUnitsRequest,
  IMappingUnitRequestDto,
} from '../types';

const STORAGE_KEY = 'units_mapping_selections';

/**
 * Load selections from localStorage
 */
const loadSelectionsFromStorage = (): IUnitSelection[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the structure
      if (Array.isArray(parsed) && parsed.every(item => 
        typeof item.unitId === 'number' &&
        typeof item.userChannelId === 'number' &&
        typeof item.channel === 'string'
      )) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load mapping selections from storage:', e);
  }
  return [];
};

/**
 * Save selections to localStorage
 */
const saveSelectionsToStorage = (selections: IUnitSelection[]): void => {
  try {
    if (selections.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
    }
  } catch (e) {
    console.warn('Failed to save mapping selections to storage:', e);
  }
};

/**
 * Custom hook for managing unit selection and mapping logic
 * Handles multi-select across channels and merge validation
 * Persists selections to localStorage for auto-save functionality
 */
export const useUnitsMapping = () => {
  // Initialize state from localStorage
  const [selectedUnits, setSelectedUnits] = useState<IUnitSelection[]>(() => 
    loadSelectionsFromStorage()
  );

  // Persist selections to localStorage whenever they change
  useEffect(() => {
    saveSelectionsToStorage(selectedUnits);
  }, [selectedUnits]);

  /**
   * Check if a unit is currently selected
   */
  const isUnitSelected = useCallback((unitId: number, channel: ChannelName): boolean => {
    return selectedUnits.some(u => u.unitId === unitId && u.channel === channel);
  }, [selectedUnits]);

  /**
   * Toggle unit selection - only ONE unit per channel allowed
   * If selecting a new unit, it replaces any existing selection for that channel
   * If clicking the same unit, it deselects it
   */
  const toggleUnitSelection = useCallback((unit: IUnitSelection) => {
    setSelectedUnits(prev => {
      const exists = prev.some(u => u.unitId === unit.unitId && u.channel === unit.channel);
      if (exists) {
        // Deselect if clicking the same unit
        return prev.filter(u => !(u.unitId === unit.unitId && u.channel === unit.channel));
      }
      // Remove any existing selection for this channel, then add the new one
      const withoutChannel = prev.filter(u => u.channel !== unit.channel);
      return [...withoutChannel, unit];
    });
  }, []);

  /**
   * Select a unit (add to selection if not already selected)
   */
  const selectUnit = useCallback((unit: IUnitSelection) => {
    setSelectedUnits(prev => {
      const exists = prev.some(u => u.unitId === unit.unitId && u.channel === unit.channel);
      if (exists) {
        return prev;
      }
      return [...prev, unit];
    });
  }, []);

  /**
   * Deselect a unit (remove from selection)
   */
  const deselectUnit = useCallback((unitId: number, channel: ChannelName) => {
    setSelectedUnits(prev => 
      prev.filter(u => !(u.unitId === unitId && u.channel === channel))
    );
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setSelectedUnits([]);
  }, []);

  /**
   * Get unique channels from current selection
   */
  const selectedChannels = useMemo((): ChannelName[] => {
    return [...new Set(selectedUnits.map(u => u.channel))];
  }, [selectedUnits]);

  /**
   * Check if merge is allowed (requires at least 1 unit selected)
   * Note: Only 1 unit per channel is enforced by toggleUnitSelection
   */
  const canMerge = useMemo((): boolean => {
    return selectedUnits.length >= 1;
  }, [selectedUnits]);

  /**
   * Get selection count
   */
  const selectionCount = useMemo((): number => {
    return selectedUnits.length;
  }, [selectedUnits]);

  /**
   * Get selections grouped by channel
   */
  const selectionsByChannel = useMemo(() => {
    return {
      airbnb: selectedUnits.filter(u => u.channel === 'airbnb'),
      booking: selectedUnits.filter(u => u.channel === 'booking'),
      gathern: selectedUnits.filter(u => u.channel === 'gathern'),
    };
  }, [selectedUnits]);

  /**
   * Generate API payload for mapping request
   * Returns array of MappingUnitRequestDto matching Swagger API spec
   */
  const getMapPayload = useCallback((): IMapUnitsRequest => {
    return selectedUnits.map((unit): IMappingUnitRequestDto => ({
      userChannelId: unit.userChannelId,
      channelType: unit.channelType,
      channelListingId: unit.channelListingId,
      propertyId: null,
    }));
  }, [selectedUnits]);

  /**
   * Helper to convert ChannelType enum to ChannelName string
   */
  const channelTypeToName = useCallback((channelType: ChannelType): ChannelName => {
    switch (channelType) {
      case ChannelType.Airbnb:
        return 'airbnb';
      case ChannelType.Booking:
        return 'booking';
      case ChannelType.Gathern:
        return 'gathern';
      default:
        return 'airbnb';
    }
  }, []);

  return {
    // State
    selectedUnits,
    
    // Selection actions
    isUnitSelected,
    toggleUnitSelection,
    selectUnit,
    deselectUnit,
    clearSelection,
    
    // Computed values
    selectedChannels,
    canMerge,
    selectionCount,
    selectionsByChannel,
    
    // API helpers
    getMapPayload,
    channelTypeToName,
  };
};
